import React from 'react';
import { NAMEPLATE_COLORS, NERVE_BLOCK_BORDER, PEDIATRIC_BORDER, PEDIATRIC_AGE_MAX } from './staffNameplates.ts';

// Collapsible legend explaining the nameplate/brick colors used across the board and bullpen.
const Swatch: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
    <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: color, flexShrink: 0, boxShadow: '0 1px 1px rgba(0,0,0,0.2)' }} />
    <span style={{ fontSize: '0.72em', color: 'var(--text-secondary)' }}>{label}</span>
  </div>
);

// A case-block BORDER (hollow box) rather than a fill — mirrors the yellow / hot-pink case borders.
const BorderSwatch: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
    <span style={{ width: '14px', height: '14px', borderRadius: '3px', border: `2px solid ${color}`, flexShrink: 0 }} />
    <span style={{ fontSize: '0.72em', color: 'var(--text-secondary)' }}>{label}</span>
  </div>
);

const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '8px' }}>
    <div style={{ fontSize: '0.62em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: 'var(--text-faint)', marginBottom: '4px' }}>{title}</div>
    {children}
  </div>
);

const ColorLegend: React.FC = () => (
  <details style={{ marginBottom: '12px', border: '1px solid var(--border-soft)', borderRadius: '6px', padding: '8px 10px', background: 'var(--bg-surface-raised)' }}>
    <summary style={{ cursor: 'pointer', fontSize: '0.8em', fontWeight: 600, color: 'var(--text-secondary)' }}>Color legend</summary>
    <div style={{ marginTop: '8px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
      <Group title="CRNA — by shift end">
        <Swatch color={NAMEPLATE_COLORS.crnaOut3pm} label="Out 3pm" />
        <Swatch color={NAMEPLATE_COLORS.crnaOut5pm} label="Out 5pm" />
        <Swatch color={NAMEPLATE_COLORS.crnaOut7pm} label="Out 7pm / 3–11pm" />
        <Swatch color={NAMEPLATE_COLORS.crnaOvernight} label="11pm–7am" />
      </Group>
      <Group title="Anesthesiologist">
        <Swatch color={NAMEPLATE_COLORS.docSupervise} label="Supervising CRNA(s)" />
        <Swatch color={NAMEPLATE_COLORS.docSolo} label="Solo in a room" />
        <Swatch color={NAMEPLATE_COLORS.docReliefNeeded} label="Out 3pm/5pm — needs relief" />
      </Group>
      <Group title="Student">
        <Swatch color={NAMEPLATE_COLORS.studentNew} label="New (no solo)" />
        <Swatch color={NAMEPLATE_COLORS.studentIntermediate} label="Intermediate" />
        <Swatch color={NAMEPLATE_COLORS.studentGreen} label="Green (can solo 2:1)" />
      </Group>
      <Group title="Other">
        <Swatch color={NAMEPLATE_COLORS.request} label="Request case" />
        <Swatch color={NAMEPLATE_COLORS.multiStart} label="Covers 2 early rooms" />
      </Group>
      <Group title="Case border — hover a case ⚑">
        <BorderSwatch color={NERVE_BLOCK_BORDER} label="Nerve block" />
        <BorderSwatch color={PEDIATRIC_BORDER} label={`Pediatric (age ≤ ${PEDIATRIC_AGE_MAX})`} />
      </Group>
    </div>
  </details>
);

export default ColorLegend;
